import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm, { summaryShape } from './CheckoutForm';
import { trackFunnel, trackPaymentFailure, FUNNEL_EVENTS } from '../../../utils/trackFunnel';

// Stripe V2 checkout island. Creates the PaymentIntent server-side (amount is
// computed from the cart, never the client), then mounts a single Stripe
// Payment Element (card + wallets + Link) and commits the order on success.
const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY);

const StoreCheckout = ({
  paymentIntentUrl, ordersUrl, completeUrl, reviewUrl, email, total, summary, csrfToken,
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);

  // Funnel log: the parent reached checkout. The customer and cart come from
  // the session server-side; this island has no ids of its own to send.
  useEffect(() => {
    trackFunnel(FUNNEL_EVENTS.CHECKOUT_VIEWED, { properties: { surface: 'shop_store_checkout' } });
  }, []);

  useEffect(() => {
    let active = true;
    fetch(paymentIntentUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
    })
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        if (d.error) {
          trackPaymentFailure('stripe', { stage: 'create_payment_intent', error: d.error });
          setError(d.error);
        } else setClientSecret(d.client_secret);
      })
      .catch((e) => {
        trackPaymentFailure('stripe', { stage: 'create_payment_intent', error: e });
        if (active) setError('Could not start checkout. Please try again.');
      });
    return () => { active = false; };
  }, [paymentIntentUrl, csrfToken]);

  if (error) {
    return (
      <div className="alert alert-danger">
        {`${error} `}
        <a href={reviewUrl}>Back to your order</a>
      </div>
    );
  }
  if (!clientSecret) return <p className="text-muted">Preparing secure checkout…</p>;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <CheckoutForm
        ordersUrl={ordersUrl}
        completeUrl={completeUrl}
        email={email}
        total={total}
        summary={summary}
        csrfToken={csrfToken}
      />
    </Elements>
  );
};

StoreCheckout.propTypes = {
  paymentIntentUrl: PropTypes.string.isRequired,
  ordersUrl: PropTypes.string.isRequired,
  completeUrl: PropTypes.string.isRequired,
  reviewUrl: PropTypes.string.isRequired,
  email: PropTypes.string,
  total: PropTypes.number,
  summary: summaryShape,
  csrfToken: PropTypes.string.isRequired,
};

StoreCheckout.defaultProps = {
  email: '',
  total: 0,
  summary: null,
};

export default StoreCheckout;
